import * as React from 'react';
import {connect} from 'react-redux';
import {IFamilyMember} from '@app-types/websocket/family.types';
import {WithStyles, withStyles} from '@material-ui/core';
import TranslateNew from '@translate/TranslateNew';
import {sharedMiscStyle} from './sharednote.style';
import AddIcon from "@material-ui/icons/Add";
import {FamilyMemberType} from '@services/family/family.constants';
import {displayErrorMessage} from '@services/user/user.actions';
import {default as ListIcon} from '@material-ui/icons/List';
import * as EventActions from '@services/events/event.action';
import * as SharedNoteActions from '@services/sharednote/sharednote.actions';
import {IAiNote, IListItem, ISharedNote} from '@app-types/websocket/sharedinfo.types';
import SharedNoteListItem from './SharedNoteListItem';
import SharedNote from './SharedNote';
import { AI_RESULT, AI_TASK_TYPE, IAiTask } from '@app-types/websocket/event.types';
import AiNoteModal from './AiNoteModal';
import { blueBackgroundColor, styleColors } from 'src/main.style';

const tn: TranslateNew = TranslateNew.getInstance();

interface IProps extends WithStyles<typeof sharedMiscStyle> {
  userRole: FamilyMemberType;
  userId: number;
  family: IFamilyMember[];
  hideFAB:boolean;

  sharedNotes: ISharedNote[];
  localNotes: ISharedNote[];

  rootClass:string;
  noteTypes:"NOTES"|"ACCOUNTS";

  aiTasks:IAiTask[],
  iconAiTasks:IAiTask[],
  localSave: (sharedNote: ISharedNote) => void;
  updateSharedNote: (sharedNote: ISharedNote) => void;
  dismissAiTask : (aiID:number) => void;
  displayError: (msg: string) => void;
  seenSharedNote: (noteId: number) => void;

  isOnline: boolean;
}

interface IState {
  editNoteId?: number;
  nextInternalNoteId: number; // negative numbers -1, -2 and so on
  aiPromptNoteId: number|undefined;
  dismissedAiTasks: number[];
  
  fabClicked:boolean;

  sentSeen: number;
}

class SharedNotesView extends React.Component<IProps, IState> {

  constructor(props: IProps) {
    super(props);

    this.state = {
      nextInternalNoteId: (-Math.round(Date.now()/1000))+(props.noteTypes==="ACCOUNTS"?-10000000:0),
      aiPromptNoteId: undefined,
      dismissedAiTasks: [],
      fabClicked: false,
      sentSeen: 0,
    };
  }

  public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
    if (this.props.aiTasks.length > 0 && prevProps.aiTasks !== this.props.aiTasks) {
      this.applyAI();
    }
    if (this.state.editNoteId !== prevState.editNoteId && this.state.editNoteId !== undefined && this.state.editNoteId > 0 
      && this.state.sentSeen < Date.now() -1000 && this.props.isOnline) {
      const note = this.props.sharedNotes.find((n) => n.noteId === this.state.editNoteId);
      if (note !== undefined && note.seenBy !== undefined && note.seenBy.indexOf(this.props.userId) === -1) {
        this.setState({sentSeen:Date.now()});
        this.props.seenSharedNote(note.noteId);
      } 
    }
  }

  public componentDidMount() {
    if (this.props.aiTasks.length > 0) {
      this.applyAI();
    }
  }

  public render() {
    const {classes, hideFAB, family, userId} = this.props;

    const theList: JSX.Element[] = [];

    for (const note of this.props.localNotes) {

      if (note.accountPassword === undefined && this.props.noteTypes === "ACCOUNTS") {
        continue; // skip notes that are not accounts if we are in account mode
      }
      if (note.accountPassword !== undefined && this.props.noteTypes === "NOTES") {
        continue; // skip notes that are not notes if we are in note mode
      }

      let teaser:string = "";
      if (note.listItems !== undefined) {
        for (let index = 0; index < Math.min(note.listItems.length, 3); index++) {
          const element = note.listItems[index];
          if (index > 0) {
            teaser = teaser + ", ";
          }
          teaser = teaser + element.val;
        }
      }

      if (teaser.length === 0 && note.text !== undefined && note.text.length > 0) {
        teaser = note.text.length > 50 ? note.text.substring(0, 50) : note.text;
      }

      theList.push(<SharedNoteListItem 
        deleted={false}
        key={""+note.noteId}
        noteId={note.noteId}
        icon={note.icon}
        avatar={note.avatarId}
        lastTime={tn.lang.now}
        onClick={() => {this.setState({editNoteId:note.noteId})}}
        selectedSharedNoteId={this.state.editNoteId}
        title={note.serviceName}
        badge={true}
        seenBy={[]}
        family={family}
        teaserText={teaser}
        iconIsBeeingGenerated={this.props.iconAiTasks.find((t) => t.refObject === note.noteId && t.status === AI_RESULT.IN_PROGRESS) !== undefined}
        isBeeingGenerated={this.props.aiTasks.find((t) => t.refObject === note.noteId && t.status === AI_RESULT.IN_PROGRESS) !== undefined}
      />);
    }

    for (const note of this.props.sharedNotes) {

      if (note.accountPassword === undefined && this.props.noteTypes === "ACCOUNTS") {
        continue; // skip notes that are not accounts if we are in account mode
      }
      if (note.accountPassword !== undefined && this.props.noteTypes === "NOTES") {
        continue; // skip notes that are not notes if we are in note mode
      }

      let teaser:string = "";
      if (note.listItems !== undefined) {
        for (let index = 0; index < Math.min(note.listItems.length, 3); index++) {
          const element = note.listItems[index];
          if (index > 0) {
            teaser = teaser + ", ";
          }
          teaser = teaser + element.val;
        }
      }
      
      if (teaser.length === 0 && note.text !== undefined && note.text.length > 0) {
        teaser = note.text.length > 50 ? note.text.substring(0, 50) : note.text;
      }

      theList.push(<SharedNoteListItem 
        deleted={false}
        key={""+note.noteId}
        noteId={note.noteId}
        icon={note.icon}
        family={family}
        avatar={note.avatarId}
        lastTime={this.getModified(note.lastUpdate)}
        onClick={() => {this.setState({editNoteId:note.noteId})}}
        selectedSharedNoteId={this.state.editNoteId}
        title={note.serviceName}
        seenBy={note.seenBy.map((accId) => family.find((mem) => mem.id === accId))}
        badge={!note.seenBy.includes(userId)}
        teaserText={teaser}
        iconIsBeeingGenerated={this.props.iconAiTasks.find((t) => t.refObject === note.noteId && t.status === AI_RESULT.IN_PROGRESS) !== undefined}
        isBeeingGenerated={this.props.aiTasks.find((t) => t.refObject === note.noteId && t.status === AI_RESULT.IN_PROGRESS) !== undefined}
      />);
    }

    let fabClasses = this.props.classes.FAB
    const fabStyle: React.CSSProperties = {right: '20px', scale:((hideFAB||this.state.editNoteId)?"0":"1"), 
          pointerEvents:((hideFAB||this.state.editNoteId)?"none":"auto")}
    let fabClickAction = () => {this.setState({fabClicked:!this.state.fabClicked});}
    let fabContent = <AddIcon style={{fontSize:'42px'}}/>

    if (this.props.noteTypes === "ACCOUNTS") {
      fabClickAction = () => {this.addNewNote(true);}
    } else if (this.state.fabClicked) {
      fabContent = <div className={classes.FABcontent + " " + classes.aiSelectInner} style={{backgroundColor: 'transparent'}}>
        <div className={classes.aiSelectButton} onClick={() => {
          this.addNewNote(true);
          this.setState({fabClicked:false}); }}>
          <AddIcon /><span className={classes.aiText}>{tn.lang.list}</span>
        </div>
        <div className={classes.aiSelectButton} onClick={() => {this.setState({fabClicked:false, aiPromptNoteId:0});}}>
          <span style= {{fontSize: '12px', minWidth: '24px', textAlign: 'center'}}>Ai</span>
          <span className={classes.aiText}>{tn.lang.prompt}</span>
        </div>
      </div>
      fabClickAction = () => {return;};
      fabClasses = classes.FAB+" "+classes.aiSelectFAB 
    }


    return (
      <>
        {(theList.length === 0) &&
          <div style={{display:'flex', flexDirection:'column', position: 'absolute', top: 'calc(50% - 175px)', 
            left: 'calc(50% - 120px)', width:'240px', alignItems:'center'}}>
            <p className={classes.helpText} style={{whiteSpace: 'pre-wrap'}}>{this.props.noteTypes === "NOTES"
              ?tn.lang.sharedNoteHelper:tn.lang.sharedAccountHelper}</p>
            <img src={'arrow.png'} style={{ width: '60px'}} alt="Arrow" />
          </div>}
        <AiNoteModal close={() => this.setState({aiPromptNoteId:undefined})} noteId={this.state.aiPromptNoteId} 
          createTemplate={(text, list) => this.addNewNote(true, text, list)}
          createNew={(callBack:(id:number) => void) => {const nId = this.addNewNote(false); callBack(nId);}} family={this.props.family}/>
        <SharedNote family={family} editNoteId={this.state.editNoteId} localNotes={this.props.localNotes} 
          prompt={() => this.setState({aiPromptNoteId:this.state.editNoteId})} isAccountNote={this.props.noteTypes === 'ACCOUNTS'}
          backgroundColor={this.props.noteTypes === 'ACCOUNTS'?blueBackgroundColor:styleColors.greyLight2}
          sharedNotes={this.props.sharedNotes} close={() => this.setState({editNoteId:undefined})}/>
        <div  className={classes.sharedNoteList} style={theList.length > 0
          ?{userSelect: 'none', WebkitUserSelect: 'none'}
          :{maxHeight:0, padding:0}} >
          {theList}
        </div>

        <div className={fabClasses} style={fabStyle} onClick={fabClickAction}>{fabContent}</div>
        <div style={{position: 'absolute',top: '0px',left: 0,width: '100%',height: '100%',opacity: 0, 
          zIndex: 99, pointerEvents:this.state.fabClicked?'auto':'none'}}
          onClick={() => this.setState({fabClicked:false})}
      />
      </>
    );
  }

  private applyAI() {
    for (const aiTask of this.props.aiTasks) {
      if (aiTask.id === undefined || aiTask.dismissed === true) {
        continue;
      }
      if (this.state.dismissedAiTasks.includes(aiTask.id)) {
        continue;
      } 
      if (aiTask.taskType === AI_TASK_TYPE.NOTE && aiTask.refObject !== undefined && aiTask.refObject !== 0) {
        const foundNote: ISharedNote | undefined = this.props.sharedNotes.find((n) => n.noteId === aiTask.refObject)
          || this.props.localNotes.find((n) => n.noteId === aiTask.refObject);
  
        if (foundNote !== undefined) {
          const note:ISharedNote = JSON.parse(JSON.stringify(foundNote));
          if (aiTask.id !== undefined && aiTask.status === AI_RESULT.SUCCESS && aiTask.result !== undefined) {
            const aiNote = JSON.parse(aiTask.result) as IAiNote;
            note.seenBy = [];
            note.text = aiNote.text?aiNote.text:"";
            note.listItems = aiNote.list
              ?this.ensureAddPossible(aiNote.list.map((item) => ({val: item.title, format: item.header ? 2 : 0})))
              :[{val: "", format: 0}]

            if (aiNote.title !== undefined && aiNote.title.length > 0) {
              note.serviceName = aiNote.title;
            }
            if (note.noteId > 0) {
              this.props.updateSharedNote(note);
            } else {
              note.lastUpdate = Date.now();
              this.props.localSave(note);
            }
            this.props.dismissAiTask(aiTask.id);
            this.setState({dismissedAiTasks: [...this.state.dismissedAiTasks, aiTask.id]});
          } else if (aiTask.id !== undefined && (aiTask.status === AI_RESULT.FAIL || aiTask.status === AI_RESULT.NO_DATA)) {
            this.props.dismissAiTask(aiTask.id);
            this.setState({dismissedAiTasks: [...this.state.dismissedAiTasks, aiTask.id]});
          } else if (aiTask.id !== undefined && (aiTask.status === AI_RESULT.SUCCESS && aiTask.result === undefined)) {
            this.props.dismissAiTask(aiTask.id);
            this.setState({dismissedAiTasks: [...this.state.dismissedAiTasks, aiTask.id]});
          } 
        }
      }
    }
  }


  private addNewNote(openIt:boolean, text?:string, list?:IListItem[]):number {

    const newNote: ISharedNote = {
      serviceName: "",
      serviceURL: this.props.noteTypes === 'ACCOUNTS' ? "" : undefined,
      accountName: this.props.noteTypes === 'ACCOUNTS' ? "" : undefined,
      accountPassword: this.props.noteTypes === 'ACCOUNTS' ? "" : undefined,
      text,
      listItems: this.props.noteTypes !== 'ACCOUNTS' ? (list?list:[{val: ""}]) : undefined,
      visibleFor: this.props.family
        .filter((mem) => (mem.role === FamilyMemberType.CHILD || mem.role === FamilyMemberType.PARENT || mem.role === FamilyMemberType.VIEWER))
        .map((mem) => mem.id),
      seenBy: [this.props.userId],
      noteId: this.state.nextInternalNoteId,
      avatarId: undefined,
      icon:undefined,
    };
    this.setState({ nextInternalNoteId: (this.state.nextInternalNoteId - 1), editNoteId: openIt?newNote.noteId:undefined });

    this.props.localSave(newNote);

    return this.state.nextInternalNoteId;
  }

  private getModified(time:number|undefined):string {
    if (time === undefined) {
      return tn.lang.unknown;
    } else if (Date.now() - time < 60000) {
      return tn.lang.now;
    } else if (Date.now() - time < (1000*60*60*24)) {
      return (Math.round((Date.now() - time) /(1000*60*60)))+" "+tn.lang.hoursShort
    } else {
      return tn.formatDateMonthShort(new Date(time));
    }
  }
  
  private ensureAddPossible(listItems:IListItem[]):IListItem[] {
    let lastItem:IListItem|undefined ;
    for (let i = listItems.length-1; i >= 0; i--) {
      if (listItems[i].deleted !== true && listItems[i].format !== 1) {
        lastItem = listItems[i];
        break;
      }
    }

    if (lastItem === undefined || lastItem.val !== "") {
      // add empty row
      listItems.push({val: "", format: 0});
      return listItems;
    }

    return listItems
  }

}

function mapStateToProps(state: any) {
  const userId = state.user.session.data.id;
  const family: IFamilyMember[] = state.family.family;
  let userRole = FamilyMemberType.VIEWER;
  for (const mem of family) {
    if (mem.id === userId) {
      userRole = mem.role;
    }
  }  

  const localNotes: ISharedNote[] = state.notes.localSaves;
  const sharedNotes: ISharedNote[] = state.notes.sharedNotes ? state.notes.sharedNotes : []
  const aiTasks:IAiTask[] = (state.event.aiTasks?state.event.aiTasks:[])
    .filter((i: IAiTask) => (i.taskType === AI_TASK_TYPE.NOTE && i.dismissed !== true));
  const iconAiTasks:IAiTask[] = (state.event.aiTasks?state.event.aiTasks:[])
    .filter((i: IAiTask) => (i.taskType === AI_TASK_TYPE.NOTE_ICON && i.dismissed !== true));

  return {
    userRole,
    family,
    userId,
    localNotes,
    aiTasks,
    iconAiTasks,
    sharedNotes,
    isOnline:state.connection.loadCompleted,
  };
}

const mapDispatchToProps = {
  localSave: SharedNoteActions.localSave,
  updateSharedNote: SharedNoteActions.updateSharedNote,
  seenSharedNote: SharedNoteActions.seenSharedNote,
  displayError: displayErrorMessage,
  dismissAiTask : EventActions.dismissAiTask,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(sharedMiscStyle)(SharedNotesView));



// WEBPACK FOOTER //
// ./src/components/sharednotes/SharedNotesView.tsx